
import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import NavBar from '@/components/NavBar';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import ResourcesTab from '@/components/ResourcesTab';

interface Module {
  id: string;
  title: string;
  description: string | null;
  week_number: number;
}

const Onboarding = () => {
  const [modules, setModules] = useState<Module[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  useEffect(() => {
    fetchModules();
  }, []);

  const fetchModules = async () => {
    try {
      const { data, error } = await supabase
        .from('onboarding_modules')
        .select('*')
        .order('week_number', { ascending: true });

      if (error) {
        console.error('Error fetching modules:', error);
        return;
      }

      setModules(data || []);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const VideosTab = () => (
    <div className="grid gap-6">
      {modules.map((module) => (
        <div 
          key={module.id}
          onClick={() => navigate(`/onboarding/module/${module.id}`)}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:border-blue-500 transition-colors cursor-pointer"
        >
          <div className="flex items-start justify-between">
            <div>
              <h3 className="text-xl font-semibold mb-2">{module.title}</h3>
              <p className="text-gray-600">{module.description}</p>
            </div>
            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
              Week {module.week_number}
            </span>
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AppSidebar />
        <div className="flex-1 relative bg-gray-50">
          <NavBar onSignOut={() => {}} isAuthenticated={isAuthenticated} />
          <main className="p-8 pt-24">
            <div className="max-w-5xl mx-auto">
              <div className="flex items-center gap-4 mb-8">
                {isMobile && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => navigate(-1)}
                    className="shrink-0"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                )}
                <h1 className="text-3xl font-bold">Sales Onboarding Program</h1>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : (
                <Tabs defaultValue="videos" className="w-full">
                  <TabsList className="grid w-full grid-cols-2 mb-8">
                    <TabsTrigger value="videos">Videos</TabsTrigger>
                    <TabsTrigger value="resources">Resources</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="videos">
                    <VideosTab />
                  </TabsContent>
                  
                  <TabsContent value="resources">
                    <ResourcesTab />
                  </TabsContent>
                </Tabs>
              )}
            </div>
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Onboarding;
